/* eslint-disable react/jsx-closing-bracket-location */
import React, { Fragment } from "react";

import ChartColors from "../config/ChartColors";

import Typography from "../components/common/Typography/Typography";
import Doughnut from "../components/Charts/Doughnut/Doughnut/Doughnut";
import LineChart from "../components/Charts/LineChart/LineChart";
import AppLayout from "../layout/AppLayout/AppLayout";
import SubNav from "../components/Navigation/SubNav/SubNav";
import DoughnutSpecial from "../components/Charts/Doughnut/DoughnutSpecial/DoughnutSpecial";
import Card from "../components/common/Card/Card";
import DoughnutTotal from "../components/Charts/Doughnut/DoughnutTotal/DoughnutTotal";
import Table from "../components/common/Table/Table";
import QueryComponent from "../components/QueryComponent/QueryComponent";
import SkeletonDonut from "../components/Skeletons/SkeletonDonut";
import SkeletonTable from "../components/Skeletons/SkeletonTable";
import SkeletonGraph from "../components/Skeletons/SkeletonGraph";
import SitePreviewListing from "../components/Sites/SitePreviewListing/SitePreviewListing";
import TableActions from "../components/common/Table/TableActions/TableActions";
import sitesService from "../services/sites.service";
import dashboardService from "../services/dashboard.service";
import Icon from "../components/common/Icon/Icon";
import ButtonLink from "../components/common/Buttons/ButtonLink/ButtonLink";
import { VISIT_REPORT_URL } from "../config/global.config";
import PestActivityYTD from "../components/PestActivityYTD/PestActivityYTD";
import DashboardActionsList from "../components/Dashboard/DashboardActionsList/DashboardActionsList";

const Index = () => (
    <AppLayout appendTitle="Dashboard" tour="dashboard">
        <SubNav
            items={[
                {
                    label: "Open Actions",
                    href: "#open-actions",
                    icon: "bolt",
                },
                {
                    label: "Pest Activity",
                    href: "#pest-activity",
                    icon: "bug",
                },
                {
                    label: "Visit Attendance",
                    href: "#visit-attendance",
                    icon: "clipboard",
                },
                {
                    label: "Back to top",
                    href: "#",
                    icon: "chevron_up",
                },
            ]}
        />
        <section id="open-actions" className="grid grid-cols-1 gap-1 md:gap-1.5">
            <Typography as="h2" variant="subTitle">
                Open Actions
            </Typography>
            <div className="grid grid-cols-1 gap-1 md:gap-1.5 lg:grid-cols-2 xl:grid-cols-4">
                <QueryComponent
                    cacheKey="dash/all-actions"
                    service={dashboardService.getAllDashboardActions}
                    loadingFallback={
                        <Fragment>
                            <SkeletonDonut />
                            <SkeletonDonut />
                            <SkeletonDonut className="md:col-span-2" />
                        </Fragment>
                    }
                >
                    {({ data }) => {
                        const actionsByType = data?.donutData.filter(({ label }) =>
                            ["Housekeeping", "False", "Proofing", "Contractworks", "Other"].includes(label)
                        );
                        const openActions = data?.donutData.filter(({ label }) => ["Vergo", "Customer"].includes(label));
                        const ragDataset = data?.donutData.filter(({ label }) => ["Red", "Amber", "Green"].includes(label));
                        const total = data?.donutData.filter(({ label }) => label === "Total")[0]?.data || 0;
                        return (
                            <Fragment>
                                <DoughnutSpecial title="Open actions by RAG" total={total} datasets={ragDataset || []} />
                                <Doughnut title="Actions open for" datasets={openActions || []} />
                                <Doughnut title="Actions by type" datasets={actionsByType || []} className="col-span-1 md:col-span-2" />
                            </Fragment>
                        );
                    }}
                </QueryComponent>
            </div>
            <div className="grid grid-cols-1 gap-1 md:gap-1.5 lg:grid-cols-2 xl:grid-cols-3">
                <DashboardActionsList
                    title="High priority actions by Rag rating"
                    cacheKey="dash/high-priority-actions-by-rag"
                    service={dashboardService.getHighPriorityActionsByRag}
                />
                <DashboardActionsList
                    title="Longest open actions"
                    cacheKey="dash/longest-open-actions"
                    service={dashboardService.getLongestOpenActions}
                />
                <QueryComponent
                    cacheKey="dash/top-sites"
                    service={sitesService.getTopSites}
                    loadingFallback={<SkeletonTable rows={5} columns={1} />}
                >
                    {({ data }) => (
                        <Card className="space-y-1 flex flex-col">
                            <Typography as="h3" variant="smallCaps">
                                Sites with most open actions
                            </Typography>
                            <SitePreviewListing
                                sites={
                                    data
                                        ? data.sites.map((s) => ({
                                            siteId: s.SiteID,
                                            siteName: s.SiteName,
                                            actionCount: s.CountOfFindings,
                                        }))
                                        : []
                                }
                            />
                        </Card>
                    )}
                </QueryComponent>
            </div>
        </section>

        <section id="pest-activity" className="space-y-1 md:space-y-1.5">
            <Typography as="h2" variant="subTitle">
                Pest Activity
            </Typography>

            <QueryComponent
                cacheKey="dash/pest-activity-by-month"
                service={dashboardService.getPestActivityPCMGraphData}
                loadingFallback={<SkeletonGraph />}
            >
                {({ data }) => (data ? <LineChart title="Pest activity per month" data={data.chartData} /> : null)}
            </QueryComponent>

            <PestActivityYTD
                serviceGraphApiCall={dashboardService.getPestActivityGraphYTDData}
                serviceGraphCacheKey="dash/pest-activity-ytd"
                serviceDetailApiCall={dashboardService.getPestActivityDetailYTDData}
                serviceDetailCacheKey=""
            />
        </section>
        <section id="visit-attendance" className="grid grid-cols-1 gap-1 md:gap-1.5">
            <Typography as="h2" variant="subTitle">
                Visit Attendance
            </Typography>

            <div className="grid grid-cols-1 gap-1 md:gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                <QueryComponent
                    cacheKey="dash/visits/by-callout"
                    service={dashboardService.getVisitsByCallouts}
                    loadingFallback={<SkeletonDonut />}
                >
                    {({ data }) => (
                        <DoughnutTotal
                            title="Total Callouts YTD"
                            label={`${data?.count.punctual || 0} on schedule`}
                            amount={data?.count.punctual || 0}
                            total={data?.count.total || 0}
                        />
                    )}
                </QueryComponent>
                <QueryComponent
                    cacheKey="dash/visits/by-routines"
                    service={dashboardService.getVisitsByRoutines}
                    loadingFallback={<SkeletonDonut />}
                >
                    {({ data }) => (
                        <DoughnutTotal
                            title="Total Routines YTD"
                            label={`${data?.count.punctual || 0} on schedule`}
                            amount={data?.count.punctual || 0}
                            total={data?.count.total || 0}
                            color={ChartColors.plum500}
                        />
                    )}
                </QueryComponent>
                <QueryComponent
                    cacheKey="dash/visits/by-follow-up"
                    service={dashboardService.getVisitsByFollowUp}
                    loadingFallback={<SkeletonDonut />}
                >
                    {({ data }) => (
                        <DoughnutTotal
                            title="Followups YTD"
                            label={`${data?.count.punctual || 0} on schedule`}
                            amount={data?.count.punctual || 0}
                            total={data?.count.total || 0}
                            color={ChartColors.tango500}
                        />
                    )}
                </QueryComponent>
            </div>

            <QueryComponent
                cacheKey="dash/get-recent-pest-activity"
                service={dashboardService.getRecentPestActivity}
                loadingFallback={<SkeletonTable />}
            >
                {({ data }) => {
                    if (!data) {
                        return null;
                    }

                    const { activity } = data;

                    return (
                        <Card padding="large" className="space-y-1.5">
                            <Typography as="h3" variant="smallCaps">
                                Recent Pest Activity
                            </Typography>
                            <Table
                                minWidth="30rem"
                                headings={[
                                    { key: "site", label: "Site" },
                                    { key: "visit", label: "Visit" },
                                    { key: "type", label: "Pest Type", hiddenMobile: true },
                                    { key: "logged", label: "Logged", hiddenMobile: true },
                                    { key: "actions", label: "" },
                                ]}
                                data={activity}
                            >
                                {(row) => (
                                    <Table.Row key={row.VisitID}>
                                        <Table.Cell>
                                            <ButtonLink
                                                variant="emphasis"
                                                size="none"
                                                className="relative z-[2] type-emphasis !font-bold"
                                                href={`sites/${row.SiteRef}`}
                                            >
                                                {row.SiteName}
                                                <Icon icon="arrow_right" className="shrink-0" />
                                            </ButtonLink>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <ButtonLink
                                                variant="emphasis"
                                                size="none"
                                                className="relative z-[2] type-emphasis !font-bold"
                                                href={`sites/${row.SiteRef}/visits/${row.VisitID}`}
                                            >
                                                #{row.VisitID}
                                                <Icon icon="arrow_right" className="shrink-0" />
                                            </ButtonLink>
                                        </Table.Cell>
                                        <Table.Cell hiddenMobile>{row.PestType}</Table.Cell>
                                        <Table.Cell hiddenMobile>{row.DateDifference} ago</Table.Cell>
                                        <Table.Cell align="right">
                                            <TableActions
                                                buttonLabel="Download Report"
                                                items={[
                                                    {
                                                        label: "Get PDF",
                                                        href: `${VISIT_REPORT_URL}&VisitID=${row.VisitID}`,
                                                        icon: "file_pdf",
                                                        title: "Download report.",
                                                    },
                                                ]}
                                            />
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                            </Table>
                        </Card>
                    );
                }}
            </QueryComponent>
        </section>
    </AppLayout>
);

export default Index;
